// Close-task cascade — cancelling a staff task also cancels its patient-side
// counterparts client-side. Two cancels also trigger WF17: a labs-ORDER cancel
// (patient "all set on labs" email/SMS + it dates the review task) and a labs-REVIEW
// cancel (opens the Virtual-Visit pair, same as completing it).

import { inCarePeriod, templateCodeOf } from './task';

export const PATIENT_CASCADE_BY_TEMPLATE: Record<string, readonly string[]> = {
  onb_virtual_visit: ['onb_patient_televisit_booking'],
  onb_baseline_labs_order: ['onb_patient_labs_booking'],
  onb_baseline_labs_review: ['onb_patient_labs_booking'],
};

const OPEN_STATUSES = new Set(['pending', 'in_progress', 'overdue', 'scheduled']);

export interface CascadeTaskLike {
  task_id?: string | number;
  status?: string;
  title?: string;
  template_code?: string;
  template?: { template_code?: string } | null;
  context_json?: unknown;
  [key: string]: unknown;
}

export function cascadeTemplateCodes(staffTemplateCode: string | null | undefined): readonly string[] {
  if (!staffTemplateCode) return [];
  return PATIENT_CASCADE_BY_TEMPLATE[staffTemplateCode] ?? [];
}

// Open patient tasks that auto-cancel when the given staff task is closed, in that task's own care period when known.
export function relatedPatientTasks(
  staffTemplateCode: string | null | undefined,
  patientTasks: CascadeTaskLike[] | null | undefined,
  carePeriodId?: string | number | null,
): CascadeTaskLike[] {
  const mapped = new Set(cascadeTemplateCodes(staffTemplateCode));
  if (mapped.size === 0) return [];
  return (patientTasks || []).filter((t) => {
    const code = templateCodeOf(t);
    return !!code && mapped.has(code) && !!t.status && OPEN_STATUSES.has(t.status)
      && inCarePeriod(t as { id_care_period?: unknown; care_period_id?: unknown }, carePeriodId);
  });
}

const TERMINAL_DONE = new Set(['completed', 'cancelled', 'missed']);

// The post-action poll waits for the sibling WF17 creates (the review task pre-exists at
// Day 0, so the order completion's real create is the booking task).
const COMPLETION_CREATES: Record<string, string> = {
  onb_baseline_labs_order: 'onb_patient_labs_booking',
  onb_baseline_labs_review: 'onb_virtual_visit',
};

// Resolving the labs review EITHER way opens the Virtual-Visit pair, so a cancel has a
// sibling to wait for too. A labs-order cancel creates nothing (it only re-dates the
// review task), hence no entry for it.
const CANCELLATION_CREATES: Record<string, string> = {
  onb_baseline_labs_review: 'onb_virtual_visit',
};

export function completionPollDone(
  staffTemplateCode: string | null | undefined,
  task: CascadeTaskLike,
  merged: CascadeTaskLike[] | null | undefined,
): boolean {
  if (!task?.status || !TERMINAL_DONE.has(task.status)) return false;
  const sibling = staffTemplateCode ? COMPLETION_CREATES[staffTemplateCode] : undefined;
  if (!sibling) return true;
  return (merged || []).some((x) => templateCodeOf(x) === sibling);
}

export function cancellationPollDone(
  staffTemplateCode: string | null | undefined,
  task: CascadeTaskLike,
  merged: CascadeTaskLike[] | null | undefined,
): boolean {
  if (task?.status !== 'cancelled') return false;
  const sibling = staffTemplateCode ? CANCELLATION_CREATES[staffTemplateCode] : undefined;
  if (!sibling) return true;
  return (merged || []).some((x) => templateCodeOf(x) === sibling);
}

export function buildCloseNote(actor?: { firstname?: string; lastname?: string } | null): string {
  const name = [actor?.firstname, actor?.lastname].filter(Boolean).join(' ');
  return name ? `Closed by ${name} — no longer needed` : 'Closed — no longer needed';
}

export function buildCascadeCloseNote(staffTaskTitle?: string | null): string {
  return staffTaskTitle
    ? `Auto-closed — related care-team task "${staffTaskTitle}" was closed`
    : 'Auto-closed — related care-team task was closed';
}
